import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useHistory } from 'react-router-dom';
import { log, logError, logRender } from '../../lib/Debug';
import Globals from '../../contexts/Globals';
import I18nLib from '../../lib/I18nLib';

// This is from apps google account, google cloud, recaptcha v3 classic
// https://www.google.com/recaptcha/admin
import {GoogleReCaptchaProvider} from 'react-google-recaptcha-v3';
import { RECAPTCHA_SITE_KEY } from '../../lib/Recaptcha';

import Signup from './Signup';
import LoaderButton from '../LoaderButton';
import PasswordRevealButton from '../PasswordRevealButton';
import Panel from '../Panel';

import UsersLib from '../../lib/UsersLib';
import BlackoutAPI from '../../lib/BlackoutAPI';
import FacebookAPI from '../../lib/FacebookAPI';
import util from '../../lib/util';

function Login(props) {
	
	// Router
	let history = useHistory();
	let location = useLocation();
	let isLoginOrSignup = ['/login','/signup'].includes(location.pathname);
	let globals = Globals.useContainer();
	let i18n = I18nLib.init(globals);
	let googleLoginElement = useRef(null);
	
	let [email,setEmail] = useState('');
	let [password,setPassword] = useState('');

	let [loadingButton,setLoadingButton] = useState(null);
	let [validationMessages,setInvalidInputs] = useState({});
	let [showPassword,setShowPassword] = useState(false);
	let [thirdPartyLoginError,setThirdPartyLoginError] = useState(null);
	let [loginPath,setLoginPath] = useState(isLoginOrSignup?location.pathname:'/login');
	
	if(isLoginOrSignup && loginPath !== location.pathname) setLoginPath(location.pathname);
	
	const validateInput = () => {
		let validInput = true;
		let validation = {};
		
		if(!email || !util.isEmail(email)){
			validation.email = i18n('login.email-required','Valid email address required');
			validInput = false;
		}

		if(!password){
			validation.password = i18n('login.password-required','Password must be provided');
			validInput = false;
		}

		setInvalidInputs(validation);
		return validInput;
	};

	// ----------------------- Initialise 3rd party login providers
	
	useEffect(()=>{

		// ----------------------- Login with Facebook

		FacebookAPI.initFB(globals.sport);

		if(globals.appleEnabled)
			window.AppleID.auth.init({
				clientId : globals.sport + '.blackout.web',
				scope : 'email',
				redirectURI : `https://${window.location.hostname}`,
				state : '',
				nonce : util.makeId(11),
				usePopup : true
			});
		
	},[globals.appleEnabled,globals.sport]);

	// ----------------------- Blackout Login Stuff

	const handleLogin = ({facebook, email, steam, apple, google}) => {
		
		setThirdPartyLoginError(null);
		
		// Set button as loading
		if(facebook && !globals.fbEnabled){
			alert(FacebookAPI.connectionErrorMessage);
			return;
		}

		if(facebook && globals.fbEnabled){
			setLoadingButton('facebook');
			if(FacebookAPI.token && (FacebookAPI.userInfo && FacebookAPI.userInfo.email)){
				loginToBR({withFacebook: true});
			} else {
				FacebookAPI.fbLogin().then(res=>{
					console.log('Login res',res);
					if(res.authResponse) {
						loginToBR({withFacebook: true});
					} else {
						setThirdPartyLoginError(i18n('login.password-required-(facebook)',`We didn't receive your email address from facebook`));
						setLoadingButton(null);
					}
				}).catch(()=>{
					setThirdPartyLoginError(i18n('login.facebook-login-failed',`Failed to authenticate with facebook`));
					setLoadingButton(null);
				});
			}
			
		} else if (apple){
			setLoadingButton('apple');
			window.AppleID.auth.signIn().then(data=>{
				console.log('login with apple data',data);
				loginToBR({withApple: true,token:data.authorization.id_token});
			}).catch(e=>{
				console.log('Sign in with Apple error',e);
				setLoadingButton(null);
			});
			
		} else if (google){
			setLoadingButton('google');
			window.auth2.attachClickHandler(googleLoginElement.current, {},
				function(googleUser) {
					loginToBR({withGoogle: true,token:googleUser.getAuthResponse().id_token});
				}, function(error) {
					if(error && error.error === 'popup_closed_by_user'){
						// Ignore
					} else {
						console.log('googleUser ERR',JSON.stringify(error, undefined, 2));
					}
					setLoadingButton(null);
				});
			googleLoginElement.current.click();
			
			// Clone the node, to remove the click handler, otherwise the click
			// handler remains and any validation checks become meaningless
			googleLoginElement.current.cloneNode(true);

		} else if (email){
			if(validateInput()){
				setLoadingButton('email');
				loginToBR({withEmail: true});
			}

		} else if(steam){
			log('Half life 3 confirmed'); // """""under development"""""
		}

	};
	
	const loginToBR = ({withFacebook=false, withEmail=false, withApple=false, withGoogle=false, token}) => {

		let include = 'clubs,social';
		
		// 'token' also returns the manager...
		if(withFacebook){
			BlackoutAPI.post({
				endpoint: 'token',
				include,
				data: {
					grant_type: 'facebook',
					access_token: FacebookAPI.token.accessToken
				}
			}).then(res=>{
				processLoginSuccess(res);
			}).catch(err=>{
				processLoginFailure(err,{facebook:true});
			});
		} else if(withApple){
			BlackoutAPI.post({
				endpoint: 'token',
				include,
				data: {
					grant_type: 'apple',
					access_token: token
				}
			}).then(res=>{
				processLoginSuccess(res);
			}).catch(err=>{
				processLoginFailure(err,{apple:true});
			});
		} else if(withGoogle){
			BlackoutAPI.post({
				endpoint: 'token',
				include,
				data: {
					grant_type: 'google',
					access_token: token
				}
			}).then(res=>{
				processLoginSuccess(res);
			}).catch(err=>{
				processLoginFailure(err,{google:true});
			});
		} else if (withEmail){
			BlackoutAPI.post({
				endpoint: 'token',
				include,
				data: {
					grant_type: 'password',
					email,
					password,
				}
			}).then(res=>{
				processLoginSuccess(res);
			}).catch(err=>{
				processLoginFailure(err,{});
			});
		}
	};

	const processLoginSuccess = res => {
		
		// Unset button as loading
		setLoadingButton(null);

		let token = {
			accessToken: res.access_token,
			expiresIn: res.expires_in,
			refreshToken: res.refresh_token,
			type: res.token_type
		};
		
		let userState = UsersLib.processUser(res.manager,token);
		UsersLib.storeToken(token,res.manager.data);
		globals.mergeUserState(userState);
		history.push(props.initialPath || '/profile');
		
		// Clear any inviter
		localStorage.removeItem('inviter');
		globals.setInviter(null);
		
	};

	const processLoginFailure = (err, opts) => {
		
		// Unset button as loading
		setLoadingButton(null);
		
		const errorCode = err.response && err.response.data && err.response.data.errors && err.response.data.errors.code;
		
		// User-facing errors
		if(errorCode === 'userNotFound'){
			setThirdPartyLoginError(i18n('login.userNotFound',`Your email address was not found in our system`));
		} else if(errorCode === 'emailPermissionNotGiven'){
			setThirdPartyLoginError(i18n('login.emailPermissionNotGiven',`Email address permission not given`));
		} else if(errorCode === 'noClubsForSport') {
			setInvalidInputs({ wrongDetails: 'No ' + globals.sport + ' club found for this email address'});
		}
		// Generic errors based on provider
		else if(opts.facebook){
			logError(err);
			setThirdPartyLoginError(i18n('login.facebook-login-failed',`Failed to authenticate with facebook`));
		} else if(opts.apple){
			logError(err);
			setThirdPartyLoginError(i18n('login.apple-login-failed',`Failed to authenticate with Apple`));
		} else if(opts.google){
			logError(err);
			setThirdPartyLoginError(i18n('login.google-login-failed',`Failed to authenticate with Google`));
		} else {
			setInvalidInputs({ wrongDetails: i18n('login.failure-message','Invalid email or password')});
		}
		
	};

	const toggleLoginOrSignup = () => {
		setThirdPartyLoginError(null);
		if(location.pathname === '/signup'){
			setLoginPath('/login');
			history.push('/login');
		} else {
			setLoginPath('/signup');
			history.push('/signup');
		}
	};

	const togglePassword = () => {
		let classList = document.getElementById('eyeCon').classList;
		if(showPassword){
			setShowPassword(false);
			classList.remove('icon-eye');
			classList.add('icon-eye-off');
		} else {
			setShowPassword(true);
			classList.remove('icon-eye-off');
			classList.add('icon-eye');
		}
	};

	// updates input states
	const handleChange = (e) => {
		if(e.target.id === 'email'){
			email = e.target.value;
			setEmail(email);
		}
		else if(e.target.id === 'password'){
			setPassword(e.target.value);
		}
	};

	const handleFocus = (e) => {
		if(validationMessages && validationMessages[e.target.id]){
			delete validationMessages[e.target.id];
			setInvalidInputs(validationMessages);
		}
	};
	
	const handleKeyDown = (e) => {
		if (e.key === 'Enter') {
			handleLogin({email: true});
		}
	};
	
	// ----------------------- Render
	
	logRender(`Login -- render`);

	let emailLoginForm = (
		<div className="gap-after">
			<div className="sub-panel no-gap-after">
				<form onSubmit={e=>e.preventDefault()}>
					<div className="input-container">
						<input 
							id='email'
							type='text'
							onChange={handleChange}
							onFocus={handleFocus}
							defaultValue={email}
							placeholder={i18n('login.email','Email')}
							autoComplete='username'
						/>
						{validationMessages
							&& validationMessages.email
							? <span className="validation-text"><span className="icon-attention" />{validationMessages.email}</span>
							: ''
						}
					</div>

					<div className="input-container">
						<div className="password-input">
							<input 
								id='password'
								type={ showPassword ? 'text' :'password'}
								onChange={handleChange}
								onKeyDown={handleKeyDown}
								onFocus={handleFocus}
								defaultValue={password}
								placeholder={i18n('login.password','Password')}
								autoComplete='current-password'
							/>
							<PasswordRevealButton onClick={()=>togglePassword()}/>
						</div>

						{validationMessages
							&& validationMessages.password
							? <span className="validation-text"><span className="icon-attention" />{validationMessages.password}</span>
							: ''
						}
					</div>
					<LoaderButton
						onClick={()=>handleLogin({email: true})}
						className="loader-button"
						isLoading={loadingButton === 'email'}
						disabled={loadingButton !== null && loadingButton !== 'email'}
					>{i18n('login.login','Log in')}</LoaderButton>
					{validationMessages
						&& validationMessages.wrongDetails
						? <span className="validation-text">
							<span className="icon-attention" />
								{validationMessages.wrongDetails}
							</span>
						: ''
					}
				</form>
				<div className="gap-before sm">
					<Link to={`/forgotpassword`} className="upper-link">{i18n('login.forgot-password','Forgot Password?')}</Link>
				</div>
			</div>
		</div>

	);

	let fbLoginButton = (
		<div className="gap-after">
			<LoaderButton
				onClick={()=>{handleLogin({facebook: true});}}
				className="fb-button match-sub-panel-side-padding"
				isLoading={loadingButton === 'facebook'}
				disabled={loadingButton !== null && loadingButton !== 'facebook'}
			>
				<span className="icon-facebook fb-button-content"></span>
				<span className="fb-button-content">{i18n('login.login-with-facebook','Log in with Facebook')}</span>
			</LoaderButton>
		</div>
	);
	
	let appleLoginButton = (
		<div className="gap-after">
			<LoaderButton
				onClick={()=>{handleLogin({apple: true});}}
				className="apple-button match-sub-panel-side-padding"
				isLoading={loadingButton === 'apple'}
				disabled={loadingButton !== null && loadingButton !== 'apple'}
			>
				<svg className="icon-apple" width="17" height="20" viewBox="0 0 842.32007 1000.0001" xmlns="http://www.w3.org/2000/svg">
					<path fill="#fff" d="M824.66636 779.30363c-15.12299 34.93724-33.02368 67.09674-53.7638 96.66374-28.27076 40.3074-51.4182 68.2078-69.25717 83.7012-27.65347 25.4313-57.2822 38.4556-89.00964 39.1963-22.77708 0-50.24539-6.4813-82.21973-19.629-32.07926-13.0861-61.55985-19.5673-88.51583-19.5673-28.27075 0-58.59083 6.4812-91.02193 19.5673-32.48053 13.1477-58.64639 19.9994-78.65196 20.6784-30.42501 1.29623-60.75123-12.0985-91.02193-40.2457-19.32039-16.8514-43.48632-45.7394-72.43607-86.6641-31.060778-43.7024-56.597041-94.37983-76.602609-152.15586C10.740416 658.44309 0 598.01283 0 539.50845c0-67.01648 14.481044-124.8172 43.486336-173.25401C66.28194 327.34823 96.60818 296.6578 134.5638 274.1276c37.95566-22.53016 78.96676-34.01129 123.1321-34.74585 24.16591 0 55.85633 7.47508 95.23784 22.166 39.27042 14.74029 64.48571 22.21538 75.54091 22.21538 8.26518 0 36.27668-8.7405 83.7629-26.16587 44.90607-16.16001 82.80614-22.85118 113.85458-20.21546 84.13326 6.78992 147.34122 39.95559 189.37699 99.70686-75.24463 45.59122-112.46573 109.4473-111.72502 191.36456.67899 63.8067 23.82643 116.90384 69.31888 159.06309 20.61664 19.56727 43.64066 34.69027 69.2571 45.4307-5.55531 16.11062-11.41933 31.54225-17.65372 46.35662zM631.70926 20.0057c0 50.01141-18.27108 96.70693-54.6897 139.92782-43.94932 51.38118-97.10817 81.07162-154.75459 76.38659-.73454-5.99983-1.16045-12.31444-1.16045-18.95003 0-48.01091 20.9006-99.39207 58.01678-141.40314 18.53027-21.27094 42.09746-38.95744 70.67685-53.0663C578.3158 9.00229 605.2903 1.31621 630.65988 0c.74076 6.68575 1.04938 13.37191 1.04938 20.00505z"/>
				</svg>&nbsp;&nbsp;
				<span className="apple-button-content">{i18n('login.login-with-apple','Sign in with Apple')}</span>
			</LoaderButton>
		</div>
	);
	
	let googleLoginButton = (
		<div className="gap-after">
			<div ref={googleLoginElement}>
				<LoaderButton
					onClick={()=>{handleLogin({google: true});}}
					className="google-button match-sub-panel-side-padding"
					isLoading={loadingButton === 'google'}
					disabled={loadingButton !== null && loadingButton !== 'google'}
				>
					<img src="https://cdn.cdnlogo.com/logos/g/35/google-icon.svg" className="icon-google" alt="Google" />&nbsp;
					<span className="google-button-content">{i18n('login.signin-with-google','Sign in with Google')}</span>
				</LoaderButton>
			</div>
		</div>
	);
					
	
	let dontHaveAnAccount = (
		<div className="login">
			{i18n('login.no-account',`Don't have an account?`)}&nbsp;&nbsp;
			<button onClick={()=>{toggleLoginOrSignup();}} className="link-button upper-link">{i18n('login.signup','Signup')}</button>
		</div>
	);
	
	let alreadyHaveAnAccount = (
		<div className="login">
			{i18n('login.already-have-account',`Already have an account?`)}&nbsp;&nbsp;
			<button onClick={()=>{toggleLoginOrSignup();}} className="link-button upper-link">{i18n('login.login','Login')}</button>
		</div>
	);
	
	if(loginPath === '/signup'){
		// User is logged in with FB, but needs to create account/authorize with blackout
		return(
			<Panel>
				<GoogleReCaptchaProvider reCaptchaKey={RECAPTCHA_SITE_KEY}>
					<Signup setThirdPartyLoginError={setThirdPartyLoginError} thirdPartyLoginError={thirdPartyLoginError} fbEnabled={globals.fbEnabled} initialPath={props.initialPath} />
				</GoogleReCaptchaProvider>
				{alreadyHaveAnAccount}
			</Panel>
		);
	} else {
		return(
			<Panel>
				<div className="center-text">
					<h2>Welcome <span className="color">{FacebookAPI.userInfo ? FacebookAPI.userInfo.first_name : ''}</span></h2>
					{emailLoginForm}
					{globals.fbEnabled?fbLoginButton:null}
					{globals.appleEnabled?appleLoginButton:null}
					{globals.googleEnabled?googleLoginButton:null}
					{thirdPartyLoginError
						? <div className="validation-text center-text"><span className="icon-attention" />{thirdPartyLoginError}</div>
						: ''}
					{dontHaveAnAccount}
				</div>
			</Panel>
		);
	}
	
	
	
}

export default Login;
